/**
 * Phase 5 · Schritt 5.3: Unified Global Drag & Cooldown Guard + Inline-Draft Hygiene
 * Eliminates the historical mismatch between `window.__lastGWanttDropTime`
 * (set by Kanban DnD hooks and read by `useTaskCRUD`) and
 * `window.__gwanttLastDragEnd` (set by `useAppDndCoordinator`), and provides
 * deterministic cleanup of abandoned `isInlineNew` drafts on project/view switch.
 */

import type { FullProject, Task } from '../types';

declare global {
  interface Window {
    __isGWanttDragging?: boolean;
    __lastGWanttDropTime?: number;
    __gwanttLastDragEnd?: number;
  }
}

export function markGlobalDragStart(): void {
  if (typeof window === 'undefined') return;
  window.__isGWanttDragging = true;
}

export function markGlobalDragEnd(timestamp: number = Date.now()): void {
  if (typeof window === 'undefined') return;
  window.__isGWanttDragging = false;
  window.__lastGWanttDropTime = timestamp;
  window.__gwanttLastDragEnd = timestamp;
}

export function isGlobalDragBlocked(cooldownMs: number = 600): boolean {
  if (typeof window === 'undefined') return false;
  if (window.__isGWanttDragging) return true;

  if (typeof document !== 'undefined' && document.body) {
    const bodyDragging =
      document.body.classList.contains('is-dragging-active') ||
      document.body.getAttribute('data-dragging') === 'true';
    if (bodyDragging) return true;
  }

  const lastDrop = Math.max(
    window.__lastGWanttDropTime || 0,
    window.__gwanttLastDragEnd || 0
  );
  if (lastDrop > 0 && Date.now() - lastDrop < cooldownMs) {
    return true;
  }
  return false;
}

export function isAbandonedInlineDraft(task: Task): boolean {
  if (!task.isInlineNew) return false;
  const hasName = Boolean(task.name && task.name.trim() !== '');
  const hasAssignee = Boolean(task.assignee && task.assignee.trim() !== '');
  const hasContactPerson = Boolean(task.contactPerson && task.contactPerson.trim() !== '');
  return !hasName && !hasAssignee && !hasContactPerson;
}

/**
 * Cleans up abandoned unnamed `isInlineNew` drafts in projects that the user has navigated away from,
 * while finalizing named `isInlineNew` tasks (`isInlineNew: false`) and preserving structural sharing (`===`).
 */
export function pruneAbandonedInlineDraftsOnProjectSwitch(
  projects: FullProject[],
  previousProjectId: string,
  nextProjectId: string
): FullProject[] {
  if (!previousProjectId || previousProjectId === nextProjectId) {
    return projects;
  }

  let anyProjectChanged = false;
  const nextProjects = projects.map(project => {
    if (project.id === nextProjectId) {
      return project;
    }
    const tasks = project.tasks || [];
    const hasInlineNew = tasks.some(t => Boolean(t.isInlineNew));
    if (!hasInlineNew) {
      return project;
    }

    let projectChanged = false;
    const cleanedTasks: Task[] = [];
    for (const task of tasks) {
      if (!task.isInlineNew) {
        cleanedTasks.push(task);
        continue;
      }
      if (isAbandonedInlineDraft(task)) {
        projectChanged = true;
        continue;
      }
      // Named inline task -> finalize so it remains permanently saved
      projectChanged = true;
      cleanedTasks.push({
        ...task,
        isInlineNew: false,
      });
    }

    if (!projectChanged) {
      return project;
    }
    anyProjectChanged = true;
    return {
      ...project,
      tasks: cleanedTasks,
    };
  });

  return anyProjectChanged ? nextProjects : projects;
}

/**
 * Cleans up abandoned unnamed `isInlineNew` drafts across all projects when the user switches view modes,
 * while finalizing named `isInlineNew` tasks (`isInlineNew: false`) and preserving structural sharing (`===`).
 */
export function pruneAbandonedInlineDraftsOnViewSwitch(
  projects: FullProject[],
  previousViewMode: string,
  nextViewMode: string
): FullProject[] {
  if (!previousViewMode || previousViewMode === nextViewMode) {
    return projects;
  }
  return pruneAbandonedInlineDraftsOnProjectSwitch(
    projects,
    `__view_${previousViewMode}`,
    `__view_${nextViewMode}`
  );
}
